<template>
  <div data-app class="overflow-hidden">
    <div class="card">
      <div class="header">
        <i class="fi-sr-arrow-left" @click="handleBack()"></i>

        <div class="row">
          <div class="col-6">
            <div class="header-title">
              <h1>{{ $t('GroupsService.importedPage.header_title') }}</h1>
              <HowToBeginAndWatchAVideo videoType="syncPage" short />
            </div>

            <div class="header-subtitle">
              <h3>{{ $t('GroupsService.importedPage.header_subTitle') }}</h3>
            </div>
          </div>

          <div class="col-6" v-if="importedData" data-test-imported-data>
            <div class="csv-details" data-test-file-name>
              <span>
                <strong>
                  {{
                    $t(
                      'GroupsService.importedGroupsPageTable.importedData.name'
                    )
                  }}:
                </strong>
                {{ importedData.filename }}
              </span>

              <span data-test-status>
                <strong>
                  {{
                    $t(
                      'GroupsService.importedGroupsPageTable.importedData.status'
                    )
                  }}:
                </strong>
                {{ $t(`GroupTab.status.${importedData.status}`) }}
              </span>
              <span data-test-id>
                <strong>
                  {{
                    $t('GroupsService.importedGroupsPageTable.importedData.id')
                  }}:
                </strong>
                {{ importedData.id }}
              </span>
            </div>
            <div class="csv-details">
              <span data-test-group-amount>
                <strong>
                  {{
                    $t(
                      'GroupsService.importedGroupsPageTable.importedData.total'
                    )
                  }}:
                </strong>
                {{ importedData.groupAmount }}
              </span>
              <span data-test-amount-success>
                <strong>
                  {{
                    $t(
                      'GroupsService.importedGroupsPageTable.importedData.success'
                    )
                  }}:
                </strong>
                {{ importedData.amountSuccess }}
              </span>
              <span data-test-amount-error>
                <strong>
                  <strong>
                    {{
                      $t(
                        'GroupsService.importedGroupsPageTable.importedData.fail'
                      )
                    }}:
                  </strong>
                </strong>
                {{ importedData.amountError }}
              </span>
              <span data-test-amount-alert>
                <strong>
                  {{
                    $t(
                      'GroupsService.importedGroupsPageTable.importedData.alert'
                    )
                  }}:
                </strong>
                {{ importedData.amountAlert }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="importedData && importedData.status === 'waiting'" class="card">
      <v-spacer />
      <div class="action-buttons">
        <v-btn
          class="btn-cancel"
          :disabled="loading"
          data-test-reject-import
          small
          @click="rejectImportRecords()"
        >
          {{ $t('rejectImport.title') }}
        </v-btn>
        <v-btn
          class="btn-confirm"
          :disabled="loading"
          data-test-confirm-import
          small
          @click="handleConfirmImportRecords()"
        >
          {{ $t('confirmImport.title') }}
        </v-btn>
      </div>
    </div>

    <div class="card">
      <div id="imported-people-page">
        <div class="table-header">
          <span class="table-header-title">
            {{ $t('GroupTab.tableHeaderTitle') }}
          </span>
        </div>

        <div v-if="loading">
          <v-skeleton-loader type="table-tbody" data-test-ranking-loading />
        </div>

        <div v-else class="table-holder">
          <DataTable
            id="DataTable"
            :title="$t('GroupTab.tableHeaderTitle')"
            :headers="_headers"
            :hideHeader="true"
            :hideOption="true"
            :contentStyle="false"
            :enablePagination="false"
            :hideSearch="true"
            :items="groups"
            :itemsPerPage="pagination.limit"
            :skeletonLines="pagination.limit"
          />

          <div class="pagination">
            <v-btn
              data-test-table-pagination-previous
              v-if="pagination.page > 1"
              x-small
              tile
              icon
              @click="handlePagination(pagination.page - 1)"
            >
              <v-icon size="10">fi fi-br-angle-left</v-icon>
            </v-btn>

            <div class="current">
              <span class="label-pagination"> {{ pagination.page }} </span>
            </div>

            <v-btn
              data-test-table-pagination-next
              v-if="pagination.page < pagination.total"
              x-small
              tile
              icon
              @click="handlePagination(pagination.page + 1)"
            >
              <v-icon size="10">fi fi-br-angle-right</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <ConfirmImportModal
      :show="importDialog"
      :requiresExplicitConfirmation="_requiresExplicitConfirmation"
      @handleConfirm="confirmImportRecords()"
      @handleClose="importDialog = false"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  getProcessedGroupsById,
  getProcessedGroupsRecords,
  confirmImportRecords,
  rejectImportRecords,
} from '@/services/groups'
import { resetPaginationToFirstPage } from '@/helpers/listing-table'
import ConfirmImportModal from '../../Partials/ConfirmImportModal/ConfirmImportModal.vue'
import { GROUPS_ACTIONS } from '@/helpers/upload-actions'
import { SYNC_FLOWS } from '@/helpers/upload-flows'
import { GROUP_MODULE_ROLE_COLUMNS } from '@/helpers/group-module-roles'

import { listingFetchErrorMixin } from '@/mixins/listing-fetch-error'
export default {
  name: 'ImportedGroupPage',

  mixins: [listingFetchErrorMixin],

  data() {
    return {
      loading: true,
      importedData: null,
      importDialog: false,
      pagination: {
        page: 1,
        limit: 20,
        offset: 0,
        total: 0,
      },
      filters: {},
      groups: [],
    }
  },

  components: {
    ConfirmImportModal,
  },

  beforeMount() {
    this.$root.$on('change:filter', this.handleFilter)
  },

  mounted() {
    this.setImportedPageLayout(true)

    this.getProcessedGroupsById()
    this.getProcessedGroupsRecords()
  },

  beforeDestroy() {
    this.$root.$off('change:filter', this.handleFilter)
  },

  computed: {
    ...mapGetters(['importedPageLayout']),

    _statusOptions() {
      return [
        {
          value: 'status',
          label: this.$t('common.status'),
          type: 'listview',
          items: [
            {
              id: 'alert',
              label: this.$t(
                'GroupsService.importedGroupsPageTable.status.alert'
              ),
            },
            {
              id: 'success',
              label: this.$t(
                'GroupsService.importedGroupsPageTable.status.success'
              ),
            },
            {
              id: 'error',
              label: this.$t(
                'GroupsService.importedGroupsPageTable.status.error'
              ),
            },
          ],
        },
      ]
    },

    // Default de LEITURA (plano híbrido D-H1): processedFiles antigos sem o
    // campo flow são do fluxo legado
    _flow() {
      return this.importedData?.flow || SYNC_FLOWS.Legacy
    },

    _isLegacyFlow() {
      return this._flow === SYNC_FLOWS.Legacy
    },

    // Guard §2.5 (plano híbrido D-H10): verificação v2 marcou o arquivo como
    // criação em massa sem groupID — o confirm exige confirmação explícita
    _requiresExplicitConfirmation() {
      return this.importedData?.requiresExplicitConfirmation === true
    },

    // Colunas por flow (§3.3.3): legacy re-exibe as colunas da develop (sem
    // groupID no add-update e sem as 9 módulo+papel); v2 exibe as do branch
    _headers() {
      const isUpdateInformation =
        this.importedData?.action === GROUPS_ACTIONS.InformationUpdate

      if (this._isLegacyFlow && !isUpdateInformation) {
        return this._legacyAddUpdateHeaders()
      }

      const headers = isUpdateInformation
        ? [
            {
              text: 'ID',
              value: 'groupID',
              type: 'text',
            },
            {
              text: this.$t(
                'GroupsService.importedGroupsPageTable.headers.costCenter'
              ),
              value: 'costCenter',
              type: 'text',
            },
            {
              text: this.$t(
                'GroupsService.importedGroupsPageTable.headers.name'
              ),
              value: 'name',
              type: 'text',
            },
          ]
        : [
            {
              text: this.$t(
                'GroupsService.importedGroupsPageTable.headers.groupID'
              ),
              value: 'groupID',
              type: 'text',
            },
            {
              text: this.$t(
                'GroupsService.importedGroupsPageTable.headers.costCenter'
              ),
              value: 'costCenter',
              type: 'text',
            },
            {
              text: this.$t(
                'GroupsService.importedGroupsPageTable.headers.name'
              ),
              value: 'name',
              type: 'text',
            },
            {
              text: this.$t(
                'GroupsService.importedGroupsPageTable.headers.parentGroup'
              ),
              value: 'parentGroup',
              type: 'text',
            },
            {
              text: this.$t(
                'GroupsService.importedGroupsPageTable.headers.managers'
              ),
              value: 'managers',
              type: 'message-popup',
            },
            {
              text: this.$t(
                'GroupsService.importedGroupsPageTable.headers.members'
              ),
              value: 'members',
              type: 'message-popup',
            },
            // Colunas módulo+papel (G3/G4) — headers técnicos canônicos,
            // idênticos nos 3 idiomas (nunca traduzir, como enum)
            ...GROUP_MODULE_ROLE_COLUMNS.map(column => ({
              text: column,
              value: column,
              type: 'message-popup',
            })),
          ]

      headers.push(
        ...[
          {
            text: this.$t(
              'GroupsService.importedGroupsPageTable.headers.operation'
            ),
            value: 'operation',
            type: 'text',
          },
          {
            text: this.$t(
              'GroupsService.importedGroupsPageTable.headers.status'
            ),
            value: 'status',
            type: 'text-status',
          },
          {
            text: this.$t(
              'GroupsService.importedGroupsPageTable.headers.message'
            ),
            value: 'message',
            type: 'text',
          },
          {
            text: this.$t(
              'GroupsService.importedGroupsPageTable.headers.importStatus'
            ),
            value: 'importStatus',
            type: 'text-status',
          },
          {
            text: this.$t(
              'GroupsService.importedGroupsPageTable.headers.importStatusMessage'
            ),
            value: 'importStatusMessage',
            type: 'text',
          },
        ]
      )

      return headers
    },
  },

  methods: {
    ...mapActions(['setImportedPageLayout', 'setImportedFileFlow']),

    // Colunas da develop para add-update/full-hierarchy legado (R1): sem
    // groupID e sem as 9 colunas módulo+papel
    _legacyAddUpdateHeaders() {
      return [
        {
          text: this.$t(
            'GroupsService.importedGroupsPageTable.headers.costCenter'
          ),
          value: 'costCenter',
          type: 'text',
        },
        {
          text: this.$t('GroupsService.importedGroupsPageTable.headers.name'),
          value: 'name',
          type: 'text',
        },
        {
          text: this.$t(
            'GroupsService.importedGroupsPageTable.headers.parentGroup'
          ),
          value: 'parentGroup',
          type: 'text',
        },
        {
          text: this.$t(
            'GroupsService.importedGroupsPageTable.headers.managers'
          ),
          value: 'managers',
          type: 'message-popup',
        },
        {
          text: this.$t(
            'GroupsService.importedGroupsPageTable.headers.members'
          ),
          value: 'members',
          type: 'message-popup',
        },
        {
          text: this.$t(
            'GroupsService.importedGroupsPageTable.headers.operation'
          ),
          value: 'operation',
          type: 'text',
        },
        {
          text: this.$t('GroupsService.importedGroupsPageTable.headers.status'),
          value: 'status',
          type: 'text-status',
        },
        {
          text: this.$t(
            'GroupsService.importedGroupsPageTable.headers.message'
          ),
          value: 'message',
          type: 'text',
        },
        {
          text: this.$t(
            'GroupsService.importedGroupsPageTable.headers.importStatus'
          ),
          value: 'importStatus',
          type: 'text-status',
        },
        {
          text: this.$t(
            'GroupsService.importedGroupsPageTable.headers.importStatusMessage'
          ),
          value: 'importStatusMessage',
          type: 'text',
        },
      ]
    },

    async getProcessedGroupsById() {
      if (!this.$route.params.id) return

      await getProcessedGroupsById(this.$route.params.id)
        .then(({ data }) => {
          this.importedData = data
          // publica o flow do arquivo para os filtros do LateralMenu (F4)
          this.setImportedFileFlow(data?.flow || SYNC_FLOWS.Legacy)
        })
        .catch(error =>
          this.notifyListingFetchError(error, 'getProcessedGroupsById')
        )
    },

    async getProcessedGroupsRecords() {
      if (!this.$route.params.id) return

      this.loading = true

      const payload = {
        processedFileId: this.$route.params.id,
        params: {
          limit: this.pagination.limit,
          offset: this.pagination.offset,
          ...this.filters,
        },
      }

      await getProcessedGroupsRecords(payload)
        .then(({ data, headers }) => {
          this.groups = data.map(el => {
            return {
              ...el,
              groupID: el.groupID ? el.groupID : '--',
              costCenter: el.costCenter ? el.costCenter : '--',
              parentGroup: el.parentGroup ? el.parentGroup : '--',
              operation: this.$t(
                `GroupsService.importedGroupsPageTable.operation.${el.operation}`
              ),
              managers: {
                content: `${el.managers ? el.managers.length : 0} ${
                  el.managers && el.managers.length === 1
                    ? this.$t(
                        `GroupsService.importedGroupsPageTable.managers.singular`
                      )
                    : this.$t(
                        `GroupsService.importedGroupsPageTable.managers.plural`
                      )
                }`,
                type: 'list',
                label: this.$t(
                  `GroupsService.importedGroupsPageTable.managers.plural`
                ),
                list:
                  !!el.managers &&
                  Array.isArray(el.managers) &&
                  el.managers.length
                    ? el.managers.map(el => ({
                        title: el.name,
                        subtitle: `${this.$t(
                          `GroupsService.importedGroupsPageTable.code`
                        )}: ${el.remoteId}`,
                      }))
                    : [],
                width: '20rem',
              },
              members: {
                content: `${el.members ? el.members.length : 0} ${
                  el.members && el.members.length === 1
                    ? this.$t(
                        `GroupsService.importedGroupsPageTable.members.singular`
                      )
                    : this.$t(
                        `GroupsService.importedGroupsPageTable.members.plural`
                      )
                }`,
                type: 'list',
                label: this.$t(
                  `GroupsService.importedGroupsPageTable.members.plural`
                ),
                list:
                  !!el.members && Array.isArray(el.members) && el.members.length
                    ? el.members.map(el => ({
                        title: el.name,
                        subtitle: `${this.$t(
                          `GroupsService.importedGroupsPageTable.code`
                        )}: ${el.remoteId}`,
                      }))
                    : [],
                width: '20rem',
              },
              ...this.mapModuleRoleCells(el),
              status: {
                status: this.formatStatus(el.status),
                label: this.$t(
                  `GroupsService.importedGroupsPageTable.status.${el.status}`
                ),
              },
              importStatus: {
                status:
                  this.executesImport(el.operation) && el.status !== 'error'
                    ? this.formatImpotedStatus(el.importStatus)
                    : '',
                label:
                  this.executesImport(el.operation) && el.status !== 'error'
                    ? this.$t(
                        `GroupsService.importedGroupsPageTable.importStatus.${el.importStatus}`
                      )
                    : '--',
              },
              message: el.message ? el.message : '--',
            }
          })

          let total = headers['x-count'] / this.pagination.limit
          if (total % 1 !== 0) total = total + 1

          this.pagination.total = Math.trunc(total)
        })
        .catch(error =>
          this.notifyListingFetchError(error, 'getProcessedGroupsRecords')
        )
        .finally(() => {
          this.loading = false
        })
    },

    // Operações com execução real no import. Delete só executa de verdade
    // quando o ARQUIVO tem action delete (G5 conservador) — records de
    // auto-delete do full-hierarchy são informativos e nunca processados:
    // sem este gate eles exibiriam "Aguardando importação" para sempre (P2-1).
    executesImport(operation) {
      if (operation === 'delete') {
        return this.importedData?.action === GROUPS_ACTIONS.Delete
      }
      return operation === 'insert' || Boolean(operation?.includes('update'))
    },

    // Papéis por módulo (G3) como célula multi-valor tipo message-popup.
    // Subtitle por e-mail com fallback code/remoteId (G2 — planilhas antigas
    // com CPF)
    mapModuleRoleCells(record) {
      return GROUP_MODULE_ROLE_COLUMNS.reduce((cells, column) => {
        const people = Array.isArray(record[column]) ? record[column] : []

        cells[column] = {
          content: `${people.length} ${
            people.length === 1
              ? this.$t(
                  'GroupsService.importedGroupsPageTable.moduleRoles.singular'
                )
              : this.$t(
                  'GroupsService.importedGroupsPageTable.moduleRoles.plural'
                )
          }`,
          type: 'list',
          label: column,
          list: people.map(person => ({
            title: person.name,
            subtitle: person.email
              ? person.email
              : `${this.$t('GroupsService.importedGroupsPageTable.code')}: ${
                  person.remoteId
                }`,
          })),
          width: '20rem',
        }

        return cells
      }, {})
    },

    handleConfirmImportRecords() {
      this.importDialog = true
    },

    async confirmImportRecords() {
      if (!this.$route.params.id) return

      this.loading = true

      await confirmImportRecords(this.$route.params.id)
        .then(() => {
          this.handleBack()
        })
        .catch(() => {
          const message = {
            type: 'error',
            message: this.$t('confirmImport.error'),
          }

          this.$emit('alert', message)
        })
        .finally(() => {
          this.loading = false
        })
    },

    async rejectImportRecords() {
      if (!this.$route.params.id) return

      this.loading = true

      await rejectImportRecords(this.$route.params.id)
        .then(() => {
          this.handleBack()
        })
        .catch(() => {
          const message = {
            type: 'error',
            message: this.$t('rejectImport.error'),
          }

          this.$emit('alert', message)
        })
        .finally(() => {
          this.loading = false
        })
    },

    handlePagination(value) {
      this.pagination.page = value
      this.pagination.offset =
        (this.pagination.page - 1) * this.pagination.limit

      this.getProcessedGroupsRecords()
    },

    handleStatus(event) {
      const payload = {
        status: event ? event.id : null,
      }

      this.filters = {
        ...this.filters,
        ...payload,
      }

      resetPaginationToFirstPage(this.pagination)

      this.getProcessedGroupsRecords()
    },

    formatStatus(value) {
      const data = {
        alert: 'waiting',
        success: 'success',
        error: 'error',
      }

      return data[value]
    },

    formatImpotedStatus(value) {
      const data = {
        waiting: 'waiting',
        success: 'success',
        error: 'error',
        processing: 'waiting',
      }

      return data[value]
    },

    // Substitui, nunca mescla: o menu lateral emite o estado COMPLETO do
    // formulario, ou `{}` no botao "Limpar" — mesclar deixaria o "Limpar"
    // sem efeito nenhum sobre a listagem.
    handleFilter(event) {
      this.filters = { ...event }

      resetPaginationToFirstPage(this.pagination)

      this.getProcessedGroupsRecords()
    },

    handleBack() {
      this.setImportedPageLayout(false)
      this.setImportedFileFlow(null)
      this.$router.push({ name: 'GroupsPage' })
    },
  },
}
</script>
<style lang="scss" scoped src="./style.scss" />
